import { Address, fromNano } from '@ton/core'
import React, { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { BoxV0 } from '@voteboxio/core'
import { useAppContext } from '../contexts/App.context'
import { useAsyncInitialize } from '../hooks/useAsyncInitialize'
import type { BoxDto } from '../types/generated'
import { useVote } from '../hooks/useVote'
import { BoxView } from '../ui/BoxView'
import { Dialog } from '../dialogs/Dialog'
import { EnterVoteAmountDialog } from '../dialogs/EnterVoteAmountDialog'
import { TimeRemaining } from '../ui/TimeRemaining'
import { useBoxStatus } from '../hooks/useBoxStatus'
import { WalletConnectionRequiredDialog } from '../dialogs/WalletConnectionRequiredDialog'
import type { TagType } from '../ui/BoxStatusBar'
import { useClaimReward } from '../hooks/useClaimReward'
import {
  trackRewardClaimInitiated,
  trackRewardClaimSuccess,
  trackRewardClaimFailed,
  trackVoteDialogOpened,
  trackVoteDialogClosed,
  extractErrorMessage,
} from '../utils/amplitude'

export const Box: FC<{ box: BoxDto; isSingleBox?: boolean }> = ({
  box,
  isSingleBox,
}) => {
  const boxIdStr = String(box.id)
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    null,
  )
  const hasVotedRef = useRef(false)

  // Track when vote dialog opens
  useEffect(() => {
    if (selectedOptionIndex !== null) {
      trackVoteDialogOpened(boxIdStr)
      hasVotedRef.current = false
    }
  }, [selectedOptionIndex, boxIdStr])

  const resetSelectedOption = () => {
    if (selectedOptionIndex !== null) {
      trackVoteDialogClosed(boxIdStr, hasVotedRef.current)
    }
    setSelectedOptionIndex(null)
  }

  const { client, wallet, authenticated } = useAppContext()

  const boxV0OpenedContract = useAsyncInitialize(async () => {
    if (client) {
      return client.open(BoxV0.BoxV0.fromAddress(Address.parse(box.address)))
    }
    return undefined
  }, [client])

  const { sendVote, isValidVotingAmount } = useVote(
    boxV0OpenedContract,
    box.recommendedVoteGas,
  )

  const { timeLeft, ...boxStatus } = useBoxStatus(box)
  const { days, hours, minutes, isExpired } = timeLeft

  const handleOnBoxChoiceClick = (index: number) => {
    setSelectedOptionIndex(index)
  }

  const { isClaimable, claimReward } = useClaimReward({
    box: box,
    recommendedVoteGas: box.recommendedVoteGas,
  })

  const getProgressPercentage = (
    createdAt: string,
    deadline: string | number,
  ) => {
    const createdAtMs = new Date(Number(createdAt)).getTime()
    const deadlineMs = Number(deadline)
    const now = Date.now()
    const totalDuration = deadlineMs - createdAtMs
    const elapsed = now - createdAtMs

    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100)
  }

  const progressPercentage = getProgressPercentage(box.startTime, box.endTime)
  const voteItIsAlreadyEnding = days < 1 && hours < 1

  const hasUserVote = box.boxChoices.some(choice =>
    Boolean(choice.user_vote_summary),
  )

  const boxTags = (): Record<TagType, boolean> => {
    if (boxStatus.isWinner) {
      // The winning box has two states: collect the reward or the reward has already been taken
      return {
        'collect-reward': boxStatus.canClaimReward,
        'reward-collected': !boxStatus.canClaimReward,
        'no-reward': false,
        ended: true,
      }
    } else if (!boxStatus.isFinish) {
      return {
        'collect-reward': false,
        'reward-collected': false,
        'no-reward': false,
        ended: false,
      }
    }

    // For finished boxes: if user is not authenticated, only show "ended" tag
    // If authenticated and voted but didn't win, show both "no-reward" and "ended" tags
    // If authenticated but didn't vote, only show "ended" tag (no "no-reward" badge)
    return {
      'collect-reward': false,
      'reward-collected': false,
      'no-reward': authenticated && hasUserVote,
      ended: true,
    }
  }

  const handleConfirm = useCallback(
    async (amount: string) => {
      if (selectedOptionIndex === null) {
        throw new Error('Nullable selectedOptionIndex')
      }

      const result = await sendVote(selectedOptionIndex, amount)
      if (result.isSuccess) {
        hasVotedRef.current = true
      }
      return result
    },
    [selectedOptionIndex, sendVote],
  )

  return (
    <>
      <BoxView
        boxId={box.id}
        title={box.question}
        progressPercentage={progressPercentage}
        expiration={
          !isExpired && (
            <TimeRemaining
              days={days}
              hours={hours}
              minutes={minutes}
              showIcon={voteItIsAlreadyEnding}
            />
          )
        }
        boxChoices={box.boxChoices}
        onBoxChoiceClick={handleOnBoxChoiceClick}
        isExpired={isExpired}
        imgSrc={box.imageUrl}
        isSingleBox={isSingleBox}
        isUnderModeration={!box.passedModeration && !isExpired}
        tags={boxTags()}
        winnerChoiceIndex={box.winner}
        onRewardClaim={
          isClaimable
            ? async () => {
                trackRewardClaimInitiated(boxIdStr)
                try {
                  await claimReward()
                  trackRewardClaimSuccess(boxIdStr, '')
                } catch (error) {
                  console.error('Failed to claim reward:', error)
                  trackRewardClaimFailed(boxIdStr, extractErrorMessage(error))
                }
              }
            : undefined
        }
        totalParticipants={box.votedUsersCount}
        totalTonStaked={Number(
          Number(
            fromNano(
              box.boxChoices.reduce((acc, choice) => {
                return (acc += Number(choice.tonStaked))
              }, 0),
            ),
          ).toFixed(1),
        )}
      />

      {selectedOptionIndex !== null && (
        <Dialog onClose={resetSelectedOption}>
          {wallet ? (
            <EnterVoteAmountDialog
              onSubmit={handleConfirm}
              onClose={resetSelectedOption}
              isValidVotingAmount={isValidVotingAmount}
              maxValue={box.maxTonStake}
              minValue={box.minTonStake}
              boxContract={boxV0OpenedContract}
              optionIndex={selectedOptionIndex}
              boxId={boxIdStr}
            />
          ) : (
            <WalletConnectionRequiredDialog />
          )}
        </Dialog>
      )}
    </>
  )
}
